import {
  EXPORT_TYPE_NAMES,
  ExportJobTypes,
  ExportTransportTypes,
  ExportTypes,
  ReportNames,
} from 'constants/exportTypes';
import { ExportType } from 'constants/exportTypes';
import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import React, { useState } from 'react';

import ImportExportIcon from '@mui/icons-material/ImportExport';
import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'config/requests';
import { useReportExport } from 'hooks/queries/useReportExport';
import { useCheckFinanceAccess } from 'hooks/useCheckFinanceAccess';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useRecordModalById } from 'hooks/useRecordModalById';
import { useListController, useNotify } from 'react-admin';
import { Button, CreateButton } from 'shared/mui/Buttons';
import { ExportTypesButton } from 'shared/mui/Buttons/ExportTypesButton/ExportTypesButton';
import { DeleteDialog } from 'shared/mui/Dialogs';
import { Typography } from 'shared/mui/Typography';
import { ExportEditButton } from 'shared/react-admin/Export/ExportEditButton/ExportEditButton';
import { ListPage } from 'shared/react-admin/Pages/ListPage/ListPage';
import { MerchantAccountShowDialog } from 'shared/widgets/MerchantAccountShowDialog/MerchantAccountShowDialog';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';
import { ReportExportErrorDialog } from 'shared/widgets/ReportExportDialog/ReportExportErrorDialog/ReportExportErrorDialog';
import { useImportFileDetails } from 'shared/widgets/RequestsBankOpsImport/hooks/useImportFileDetails';
import { BankOperation, ImportFileStatusesValue } from 'types/embedded/bankOperation';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { Permission } from 'types/embedded/permission';

import { EXPORT_FIELDS_REQUEST_BANK_OPS } from './constants/exportFields';
import { RequestsBankOpsListCommentDialog } from './RequestsBankOpsListCommentDialog/RequestsBankOpsListCommentDialog';
import { RequestsBankOpsListDatagrid } from './RequestsBankOpsListDatagrid/RequestsBankOpsListDatagrid';
import { RequestsBankOpsListFilter } from './RequestsBankOpsListFilter/RequestsBankOpsListFilter';
import { RequestsBankOpsListImportDialog } from './RequestsBankOpsListImportDialog/RequestsBankOpsListImportDialog';
import { RequestsBankOpsImportErrorDialog } from '../components/RequestsBankOpsImportErrorDialog/RequestsBankOpsImportErrorDialog';
import { RequestsBankOpsImportProgress } from '../components/RequestsBankOpsImportProgress/RequestsBankOpsImportProgress';

function getColumnsByRoles(userRoles: Role[]) {
  return EXPORT_FIELDS_REQUEST_BANK_OPS.filter(
    (col) => !col.excludeRoles?.some((role) => userRoles.includes(role)),
  );
}

export const RequestsBankOpsList: React.FC = () => {
  const {
    filterValues,
    isLoading: isListLoading,
    total,
  } = useListController<BankOperation>({
    resource: 'bank_operations/pending_statuses',
    sort: { field: 'id', order: 'DESC' },
    queryOptions: { enabled: false },
  });
  const { hasResourcePermissions } = usePermissions();

  const notify = useNotify();

  const [exportType, setExportType] = useState<ExportType>(ExportTypes.Excel);
  const [isOpenNotifyDialog, setIsOpenNotifyDialog] = useState(false);
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);

  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getUserInfo });

  const { mutate: exportMutate, isLoading: isExportLoading } = useReportExport({
    filters: {
      ...filterValues,
      exportType,
      jobType: ExportJobTypes.Bank_Operation_Pending_Statuses,
      ...(isSendReportOnEmail && { notificationTransport: ExportTransportTypes.Email }),
    },
    preferenceKey: ReportNames.Bank_Operations_Pending_Statuses,
    onSuccess: () => {
      notify(
        `Файл экспорта отчёта по "Заявкам на банковские операции" формируется ${isSendReportOnEmail ? 'и будет отправлен вам на почту' : ''}`,
        {
          type: 'info',
        },
      );

      setIsOpenNotifyDialog(false);
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  const deleteModal = useRecordModal<BankOperation>();
  const merchantAccountModal = useRecordModalById<MerchantAccount>({
    resource: Resources.MerchantAccounts,
  });
  const commentModal = useRecordModal<BankOperation>();
  const importErrorModal = useRecordModal();
  const [openImportModal, setOpenImportModal] = useState(false);

  const { isHaveFinanceAccess } = useCheckFinanceAccess();

  const { activeImportStatus, activeImportProgress, hasActiveImport } = useImportFileDetails();

  const hasDateFilter = Object.keys(filterValues).some((key) =>
    ['dateOfOperation', 'createdAt'].some((field) => key.includes(field)),
  );

  return (
    <ListPage
      actions={[
        hasResourcePermissions(Resources.RequestBankOperations, [
          Permission.Full,
          Permission.Create,
        ]) && (
          <CreateButton disabled={hasActiveImport} key="create-button" resource="bank_operations" />
        ),
        isHaveFinanceAccess &&
          hasResourcePermissions(Resources.ImportFiles, [Permission.Full, Permission.Create]) && (
            <Button
              disabled={hasActiveImport}
              key="import-excel"
              label="Загрузить из EXCEL"
              onClick={() => setOpenImportModal(true)}
              startIcon={<ImportExportIcon />}
              variant="contained"
            />
          ),
        <ExportTypesButton
          disabled={!total || hasActiveImport}
          exportType={exportType}
          key="export-button"
          onChangeType={setExportType}
          onClick={() => {
            if (!hasDateFilter) {
              setIsOpenErrorDialog(true);
              return;
            }
            setIsOpenNotifyDialog(true);
          }}
        />,
      ]}
      empty={false}
      headerTitle={{
        titleText: 'Заявки на банковские операции',
        tooltipText:
          'Ручная операция, связанная с созданием заявки на движение денежных средств (с направлением на ввод или вывод).',
      }}
      resource="bank_operations/pending_statuses"
      sort={{ field: 'id', order: 'DESC' }}
      sx={{
        '& .MuiPaper-root': {
          overflow: 'auto',
        },
      }}
    >
      <RequestsBankOpsListImportDialog
        onClose={() => setOpenImportModal(false)}
        onError={importErrorModal.handleOpenRecordModal}
        open={openImportModal}
      />
      <RequestsBankOpsImportErrorDialog
        error={importErrorModal.record}
        onClose={importErrorModal.handleCloseRecordModal}
        open={importErrorModal.openRecordModal}
      />
      <RequestsBankOpsListCommentDialog
        onClose={commentModal.handleCloseRecordModal}
        open={commentModal.openRecordModal}
        record={commentModal.record}
      />
      <DeleteDialog
        details="Заявки на банковские операции будут удалены без возможности восстановления."
        onClose={deleteModal.handleCloseRecordModal}
        open={deleteModal.openRecordModal}
        record={deleteModal.record}
        title="Удаление заявок на банковскую операцию"
      />
      <MerchantAccountShowDialog
        isFetching={merchantAccountModal.isFetching}
        merchantAccount={merchantAccountModal.record}
        onClose={merchantAccountModal.handleCloseRecordModal}
        open={merchantAccountModal.openRecordModal}
      />
      {hasActiveImport && activeImportStatus && (
        <RequestsBankOpsImportProgress
          progress={activeImportProgress}
          status={activeImportStatus as ImportFileStatusesValue}
        />
      )}
      <RequestsBankOpsListFilter />
      <RequestsBankOpsListDatagrid
        handleOpenDeleteModal={deleteModal.handleOpenRecordModal}
        handleOpenMerchantAccountModal={merchantAccountModal.handleOpenRecordModal}
        handleOpenRecordModalComment={commentModal.handleOpenRecordModal}
      />
      <ReportExportErrorDialog
        message={
          <Typography>
            Для экспорта необходимо указать период хотя бы по одной дате:
            <br />
            Дата в ПС / период, Дата создания / период.
          </Typography>
        }
        onClose={() => setIsOpenErrorDialog(false)}
        open={isOpenErrorDialog}
        title="Ошибка экспорта"
      />
      <ReportExportDialog
        exportType={EXPORT_TYPE_NAMES[exportType]}
        isLoading={isListLoading || isExportLoading}
        onChange={() => setIsSendReportOnEmail((prev) => !prev)}
        onClose={() => setIsOpenNotifyDialog(false)}
        onSubmit={exportMutate}
        open={isOpenNotifyDialog}
        sendOnEmail={isSendReportOnEmail}
        title={
          <>
            <Typography component="span" sx={{ verticalAlign: 'middle' }} variant="inherit">
              Экспорт отчёта
            </Typography>
            <ExportEditButton
              fields={getColumnsByRoles(user?.roles || [])}
              preferenceKey={ReportNames.Bank_Operations_Pending_Statuses}
            />
          </>
        }
        userEmail={user?.corporateEmail || user?.email}
      />
    </ListPage>
  );
};
